import { useEffect, useState } from "react";
import { useTableSort } from "@hooks";
import {
  BOOKING_TYPE_FILTERS,
  type BookingTypeFilter,
} from "@components/MobileList";

export type BookingSortField = "valueDate" | "amount";

export const BOOKING_SORT_SAVE_KEY = "bookings-list-mobile";
export const BOOKING_TYPES_STORAGE_KEY = "bookings-list-mobile-types";

const DEFAULT_SORT: { field: BookingSortField; direction: TableSortDirection } =
  { field: "valueDate", direction: "DESC" };

// A broken or outdated entry falls back to no filter instead of failing.
const readTypes = (): BookingTypeFilter[] => {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(BOOKING_TYPES_STORAGE_KEY) ?? "[]"
    );
    return Array.isArray(parsed)
      ? BOOKING_TYPE_FILTERS.filter((type) => parsed.includes(type))
      : [];
  } catch {
    return [];
  }
};

/**
 * Sort and type filter of the phone bookings list, remembered per browser. The
 * server sorts and filters, as the list loads page by page; month headers only
 * make sense by date.
 */
export const useBookingListView = () => {
  const { sortField, sortDirection, sort } = useTableSort(
    DEFAULT_SORT,
    BOOKING_SORT_SAVE_KEY
  );
  const [types, setTypes] = useState<BookingTypeFilter[]>(readTypes);

  useEffect(() => {
    try {
      localStorage.setItem(BOOKING_TYPES_STORAGE_KEY, JSON.stringify(types));
    } catch {
      // Storage can be unavailable (private mode, quota); the filter still works.
    }
  }, [types]);

  const field: BookingSortField =
    sortField === "amount" ? "amount" : "valueDate";
  const direction: TableSortDirection =
    sortDirection === "ASC" ? "ASC" : "DESC";

  return {
    sortField: field,
    sortDirection: direction,
    order: `${field} ${direction.toLowerCase()}`,
    groupByMonth: field === "valueDate",
    isSortChanged:
      field !== DEFAULT_SORT.field || direction !== DEFAULT_SORT.direction,
    setSort: (nextField: BookingSortField, nextDirection: TableSortDirection) =>
      sort(nextField, nextDirection),
    types,
    setTypes,
    reset: () => {
      sort(DEFAULT_SORT.field, DEFAULT_SORT.direction);
      setTypes([]);
    },
  };
};

export type BookingListView = ReturnType<typeof useBookingListView>;
